import TableHeader from "@commons/Table/TableHeader";
import Table from "@commons/Table/TableContent";
import AddIcon from "@mui/icons-material/Add";
import { usePage } from "@hooks/usePage";
import { config } from "@/config";
import { useCallback, useEffect, useState } from "react";
import { useGetCameraTraps } from "@services/device/cameraTrap/list";
import { toast } from "react-toastify";
import { cameraTrapColumns } from "@typings/entities/devices/cameraTrap/table";
import {
  prepareCameraTrapsTable,
  type CameraTrapWithLabels,
} from "@utils/translator/device/cameraTrap";
import { isEmpty } from "@utils/formatters";
import { useNavigate } from "react-router-dom";
import { useExportCameraTraps } from "@hooks/exports/useExportCameraTraps";
import { usePermissions } from "@hooks/usePermissions";

function TableCameraTrap({ filter }: { filter?: string }) {
  const { openModal, setDataModal } = usePage();
  const { can } = usePermissions();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(config.table.defaultPageSize);

  const { handleExport, isLoading: isExporting } = useExportCameraTraps();

  const navigate = useNavigate();

  const heads = cameraTrapColumns;

  const {
    error,
    isLoading,
    data: cameraTraps,
  } = useGetCameraTraps({
    number: !isEmpty(filter) ? Number(filter) : undefined,
    limit: rowsPerPage,
    page: page + 1,
  });

  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    [],
  );

  const processedCameraTraps = prepareCameraTrapsTable(
    cameraTraps?.items || [],
  );

  useEffect(() => {
    if (error) {
      toast.error("Erro ao carregar as câmeras trap");
    }
  }, [error]);

  if (!processedCameraTraps) {
    return null;
  }

  const handleEdit = (cameraTrap: CameraTrapWithLabels) => {
    setDataModal({ id: cameraTrap.id });
    openModal("editCameraTrap");
  };

  const canCreateCameraTrap = can("create", "cameraTraps");
  const canUpdateCameraTrap = can("update", "cameraTraps");

  return (
    <div className="w-full flex flex-col justify-center items-center text-center bg-white rounded-lg shadow-md p-4">
      <TableHeader.Root>
        {canCreateCameraTrap && (
          <TableHeader.Button
            customVariant="secondary"
            startIcon={<AddIcon />}
            onClick={() => openModal("newCameraTrap")}
            className="w-full xs:w-auto"
          >
            Nova Câmera Trap
          </TableHeader.Button>
        )}
        <TableHeader.Button
          customVariant="dark"
          className="w-full xs:w-auto"
          onClick={handleExport}
          disabled={isExporting}
        >
          {isExporting ? "Exportando..." : "Exportar .csv"}
        </TableHeader.Button>
      </TableHeader.Root>

      <Table.Root isLoading={isLoading}>
        <Table.Head heads={heads} actions={true} />
        <Table.Body
          rows={processedCameraTraps}
          columns={heads.map((head) => head.id)}
          actions={canUpdateCameraTrap ? ["view", "edit"] : ["view"]}
          onView={(cameraTrap) =>
            navigate(`/devices/camera-trap/${cameraTrap.id}`)
          }
          onEdit={canUpdateCameraTrap ? handleEdit : undefined}
        />
        <Table.Footer
          colSpan={heads.length}
          count={cameraTraps?.meta.totalItems ?? 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Table.Root>
    </div>
  );
}

export default TableCameraTrap;
